import * as z from "zod/mini"; // using zod/mini to reduce bundle size

import developmentConfig from "../../../config/development.json" with { type: "json" };
import localConfig from "../../../config/local.json" with { type: "json" };
import productionConfig from "../../../config/production.json" with { type: "json" };
import stagingConfig from "../../../config/staging.json" with { type: "json" };
import testE2eConfig from "../../../config/test-e2e.json" with { type: "json" };
import testConfig from "../../../config/test-unit.json" with { type: "json" };

const Environments = ["local", "development", "staging", "production", "test", "test-e2e"] as const;

export type Config = z.infer<typeof configSchema> & {
  ilEnv: (typeof Environments)[number];
  basePath: string;
  buildVersion: string;
};

export const configSchema = z.object({
  apiBaseUrl: z.string(),
  authCookieName: z.string(),
});

const configMap: Record<Config["ilEnv"], Omit<Config, "ilEnv" | "basePath" | "buildVersion">> = {
  local: localConfig,
  development: developmentConfig,
  staging: stagingConfig,
  production: productionConfig,
  test: testConfig,
  "test-e2e": testE2eConfig,
} as const;

export const getConfig = (): Config => {
  const environment = getEnvironment();

  const basePath = getBasePath();
  const buildVersion = getBuildVersion();

  const envCfg = configMap[environment];

  const parsedEnvCfg = configSchema.parse(envCfg);

  // Optional build-time override for the API base URL — used by sandbox/preview
  // builds that need same-origin to go through the dev server proxy. When the
  // var is unset, the JSON config is the source of truth (default behaviour).
  const apiBaseUrlOverride = import.meta.env.VITE_API_BASE_URL;
  const apiBaseUrl =
    typeof apiBaseUrlOverride === "string" ? apiBaseUrlOverride : parsedEnvCfg.apiBaseUrl;

  return {
    ilEnv: environment,
    basePath,
    buildVersion,
    ...parsedEnvCfg,
    apiBaseUrl,
  };
};

/**
 * TODO: this environment detection must be implemented per case!
 *
 * the `IL_ENV` variable can be optionally used to override the detection, you should use it only for tests.
 * Check [docs](./docs/02-config.md) for more details.
 */
function getEnvironment(): Config["ilEnv"] {
  const overrideEnv = import.meta.env.IL_ENV;
  if (overrideEnv) {
    return overrideEnv as Config["ilEnv"];
  }

  const host = window.location.host;

  if (host.includes("localhost") || host.includes("local.")) {
    return "local";
  }

  if (host.includes("dev.") || host.includes("development.")) {
    return "development";
  }

  if (host.includes("stg.") || host.includes("staging.")) {
    return "staging";
  }

  return "production";
}

// Base path can be optionally set via env variable
function getBasePath(): string {
  return import.meta.env.IL_BASE_PATH ?? "/";
}

// IL_BUILD_VERSION can be optionally set via env variable and it's injected at build time in the CI step (inside CICD)
// it will have the format <git-tag>-<short-sha> e.g. v1.0.0-abc1234 and it's being used to match application version with
// observability sourcemaps
function getBuildVersion(): string {
  return import.meta.env.IL_BUILD_VERSION ?? "unknown";
}
